import React, { useState } from "react";
import { Building2, Palmtree, Layers, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const CHOICES = [
  { key: "longue", label: "Location nue ou meublée longue durée", desc: "Baux, quittances, locataires", icon: Building2, value: { longue: true, saisonnier: false } },
  { key: "saisonnier", label: "Location saisonnière / tourisme", desc: "Airbnb, Booking, Abritel, réservations directes", icon: Palmtree, value: { longue: false, saisonnier: true } },
  { key: "both", label: "Les deux", desc: "Longue durée et saisonnier côte à côte", icon: Layers, value: { longue: true, saisonnier: true } },
];

export async function saveActivities(value, user, setUser) {
  const r = await api.put("/me/activities", value);
  setUser({ ...user, ...r.data });
  return r.data;
}

export const ActivityGate = () => {
  const { user, setUser } = useAuth();
  const [sel, setSel] = useState("longue");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await saveActivities(CHOICES.find((c) => c.key === sel).value, user, setUser);
      toast.success(sel === "longue" ? "C'est noté ! Vous pourrez activer le saisonnier à tout moment." : "Module « Location saisonnière » activé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" data-testid="activity-gate">
      <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        <div className="bg-brand-greenDeep text-white p-6">
          <p className="text-xs font-semibold text-brand-lime uppercase tracking-wide mb-2">Personnalisez votre espace</p>
          <h3 className="font-display text-2xl font-semibold">Quel type de location pratiquez-vous ?</h3>
          <p className="text-sm text-white/70 mt-1.5">Nous n'affichons que les outils utiles. Modifiable à tout moment depuis la pastille en haut du menu.</p>
        </div>
        <div className="p-6 space-y-2.5">
          {CHOICES.map((c) => {
            const Icon = c.icon;
            const on = sel === c.key;
            return (
              <button key={c.key} onClick={() => setSel(c.key)} className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl border text-left transition-colors ${on ? "border-brand-limeDark bg-brand-lime/15" : "border-brand-soft hover:bg-brand-softer"}`} data-testid={`activity-choice-${c.key}`}>
                <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${on ? "bg-brand-greenDeep text-brand-lime" : "bg-brand-softer text-brand-greenDeep"}`}><Icon className="w-5 h-5" /></span>
                <span className="flex-1"><span className="block font-semibold text-brand-greenDeep text-sm">{c.label}</span><span className="block text-xs text-brand-green/55">{c.desc}</span></span>
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${on ? "border-brand-greenDeep bg-brand-greenDeep" : "border-brand-soft"}`}>{on && <Check className="w-3 h-3 text-white" />}</span>
              </button>
            );
          })}
          <button onClick={submit} disabled={busy} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm disabled:opacity-60 transition-colors" data-testid="activity-gate-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Continuer
          </button>
        </div>
      </div>
    </div>
  );
};

export const ActivitySwitch = () => {
  const { user, setUser } = useAuth();
  const acts = user?.activities || { longue: true, saisonnier: false };
  const toggle = async (k) => {
    const next = { ...acts, [k]: !acts[k] };
    if (!next.longue && !next.saisonnier) { toast.error("Gardez au moins une activité active"); return; }
    try {
      await saveActivities(next, user, setUser);
      if (k === "saisonnier") toast.success(next.saisonnier ? "Location saisonnière activée" : "Location saisonnière masquée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const pill = (k, label, Icon) => (
    <button onClick={() => toggle(k)} className={`flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${acts[k] ? "bg-brand-greenDeep text-white" : "text-brand-green/55 hover:text-brand-greenDeep"}`} aria-pressed={!!acts[k]} data-testid={`activity-switch-${k}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
  return (
    <div className="mx-3 mt-4 p-1 rounded-full bg-brand-softer border border-brand-soft flex gap-1" data-testid="activity-switch">
      {pill("longue", "Longue durée", Building2)}
      {pill("saisonnier", "Saisonnier", Palmtree)}
    </div>
  );
};
